type LocalStorageInstance<T, J extends boolean> = {
  //  This ensures that if you set jsonify to true, they're gonna be dealing with JSON strings.
  get: () => J extends true ? (T | null) : (string | null);
  set: (data: T) => void;
};

//  Overrides which clarify that if you pass jsonify, you're gonna be dealing with strings.
export default function createLocalStorageInstance<T>(key: string, jsonify?: true): LocalStorageInstance<T, true>;
export default function createLocalStorageInstance(key: string, jsonify?: false): LocalStorageInstance<string, false>;

/**
 * Create a get/set pair for a given localStorage key.
 * Also stringifies/parses JSON if that's your thing.
 *
 * @param {string} key - Local storage key
 * @param {boolean} jsonify - Whether to JSON stringify the value
 * @returns {LocalStorageInstance} - get/set pair for the given key
 * @example
 *
 * type MyDataType = Record<string, string>;
 * const ls = createLocalStorageInstance<MyDataType>('some_unique_ls_key');
 * // ...
 * const data = ls.get();
 * // ...
 * ls.set({  })
 */
export default function createLocalStorageInstance<T>(
  key: string,
  jsonify: boolean = true,
): LocalStorageInstance<T, boolean> {
  return {
    get: () => {
      const data: string | null = localStorage.getItem(key);
      if (data && jsonify) {
        return JSON.parse(data) as T;
      }
      return data;
    },
    set: (rawData: T | string): void => {
      const data: string = jsonify
        ? JSON.stringify(rawData)
        : rawData as string;
      localStorage.setItem(key, data);
    },
  };
}
